import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import DatePicker from '@amzn/meridian/date-picker';
import Loader from '@amzn/meridian/loader';
import { DEFAULT_SHIFT_OPTIONS } from '../../../../utils/constants';
import { getClockWindowNameBasedOnSelectedOption } from '../../../../services/shiftPlanService';
import { compareFlowMonitoringData } from '../api';
import { CompareFlowMonitoringDataOutput } from '../model';
import { MonitoringEntityType } from '../constants';
import { deriveVolumeProjectionSummary, formatTimestamp, formatDateInZone } from '../utils';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { selectStationCode, setStationCode } from '../../../../store/slices/shiftPlanSlice';
import { showError } from '../../../../store/slices/errorSlice';
import { useSiteGatedFeature } from '../../../../hooks/useSiteGatedFeature';
import MaestroCardsRow from './MaestroCardsRow';
import ProgressReportTable from './ProgressReportTable';
import EndOfShiftReporting from './EndOfShiftReporting';
import ShiftProgressBanner from './ShiftProgressBanner';
import ShiftTimeline from './ShiftTimeline';
import IntraShiftTrendChart from './IntraShiftTrendChart';
import InShiftEosWash from './InShiftEosWash';
import ReportPlanSelector from './ReportPlanSelector';
import { useReportPlans } from './useReportPlans';
import { resolveDefaultPlanId } from './reportPlanOptions';

type ShiftReportView = 'inShift' | 'endOfShift';

// End of Shift Reporting has its own gate, independent of RCC_SHIFT_REPORTING_V2
// (which gates the V2 dashboard + In-Shift). Resolved via the facade IsFeatureEnabled
// allowlist; fails closed so EOS stays hidden unless explicitly enabled.
const EOS_REPORTING_V2_FEATURE_NAME = 'RCC_EOS_REPORTING_V2';

// Earliest date selectable in the Date picker. Shift Progress Report data does not
// exist before this date, so earlier dates are disabled to keep users from
// requesting reports that can never return data.
const MIN_SELECTABLE_DATE = '2026-08-26';

const Dashboard: React.FC = () => {
    const dispatch = useAppDispatch();
    const stationCode = useAppSelector(selectStationCode);

    // Fallback: read stationCode from URL query params if Redux is empty
    useEffect(() => {
        if (!stationCode) {
            const searchParams = new URLSearchParams(window.location.search);
            const stationCodeParam = searchParams.get('stationCode');
            if (stationCodeParam) {
                dispatch(setStationCode({ stationCode: stationCodeParam }));
            }
        }
    }, [dispatch, stationCode]);

    // Provisional default = the viewer's local day; corrected to the SITE's local day once the
    // first response lands (its shiftTimeContext carries the site timezone). See the effect below.
    const [selectedDate, setSelectedDate] = useState<string>(() => formatDateInZone(Date.now()));
    const [shiftType, setShiftType] = useState<string>(DEFAULT_SHIFT_OPTIONS[0].value);
    const [loading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [compareResponse, setCompareResponse] = useState<CompareFlowMonitoringDataOutput | null>(null);
    // Wall-clock instant (epoch ms) when the latest backend response was received;
    // null until the first response lands. Drives the "Last updated" stamp.
    const [lastUpdatedMs, setLastUpdatedMs] = useState<number | null>(null);
    const [shiftReportView, setShiftReportView] = useState<ShiftReportView>('inShift');
    // Whether End of Shift Reporting is enabled (RCC_EOS_REPORTING_V2). null = unresolved.
    const isEosEnabled = useSiteGatedFeature(EOS_REPORTING_V2_FEATURE_NAME);
    // One-shot guard so the site-local date default is applied once per station (below), letting
    // manual date changes stick.
    const siteDateAppliedRef = useRef(false);

    // End of Shift shows both shifts via its own toggle, so the Day/Night Shift
    // selector is irrelevant there — hidden to avoid a dead, confusing control.
    // Gated by RCC_EOS_REPORTING_V2 so a stale 'endOfShift' selection can't render
    // the view when the flag is off.
    const isEndOfShift = shiftReportView === 'endOfShift' && isEosEnabled === true;

    // Plan picker (In-Shift). `selectedPlanId` stays null until the operator
    // actively picks a plan, so the default report is still the baseline the
    // backend resolves — the picker only overrides it on an explicit choice.
    const inShiftClockWindow = getClockWindowNameBasedOnSelectedOption(shiftType);
    const { groups: planGroups, loading: plansLoading } = useReportPlans({
        ofdDate: selectedDate,
        shiftTypes: [inShiftClockWindow],
        skip: !stationCode || isEndOfShift,
    });
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

    // A plan chosen for one date/shift is meaningless for another.
    useEffect(() => {
        setSelectedPlanId(null);
    }, [selectedDate, shiftType]);

    // Single source of truth for the volume projection — shared by the pace
    // banner and the Maestro VOLUME card so their numbers can never drift.
    // Snapshotted at fetch time (uses the device clock); recomputed on refetch.
    // One projection instant, shared by the pace banner and every Maestro card
    // so their numbers can't drift. Snapshotted per fetch (uses the device clock).
    const nowMs = useMemo(() => Date.now(), [compareResponse]);
    const volumeProjection = useMemo(
        () => deriveVolumeProjectionSummary(
            compareResponse?.volumeTargetComparisons,
            compareResponse?.shiftTimeContext,
            nowMs,
        ),
        [compareResponse, nowMs],
    );

    // Only the Shift Progress Report is offered now, so the report is fetched
    // automatically on mount and whenever the date, shift, or station changes —
    // there is no report selector to trigger it.
    // The only caller is the effect below, which already waits for a station, so
    // there is no "no warehouse selected" case to report from here: a page opened
    // before the station resolves just stays quiet instead of throwing a dialog.
    const fetchProgressReport = useCallback(async () => {
        setLoading(true);
        setDataLoaded(false);
        try {
            const response = await compareFlowMonitoringData({
                warehouseId: stationCode!,
                ofdDate: selectedDate,
                shiftType: getClockWindowNameBasedOnSelectedOption(shiftType),
                monitoringEntityType: MonitoringEntityType.PROCESS_PATH,
                // Only sent when the operator picked a plan; otherwise the facade
                // resolves the baseline as before.
                baselineShiftPlanId: selectedPlanId ?? undefined,
            });
            setCompareResponse(response);
            setLastUpdatedMs(Date.now());
            setDataLoaded(true);
        } catch (err: any) {
            dispatch(showError({ message: err.message || 'Failed to fetch comparison data', title: 'API Error' }));
        } finally {
            setLoading(false);
        }
    }, [selectedDate, shiftType, stationCode, selectedPlanId, dispatch]);

    useEffect(() => {
        if (stationCode) {
            fetchProgressReport();
        }
    }, [fetchProgressReport, stationCode]);

    // Re-arm the site-local date default whenever the station changes.
    useEffect(() => {
        siteDateAppliedRef.current = false;
    }, [stationCode]);

    // Default the report date to the SITE's local day, not the viewer's. The first response's
    // shiftTimeContext carries the site timezone + serverTime (already used by the progress bar),
    // so once it lands, snap the date to the site's local calendar day. One-shot per station so a
    // user's manual date change is not overridden on the next refetch.
    useEffect(() => {
        if (siteDateAppliedRef.current) return;
        const ctx = compareResponse?.shiftTimeContext;
        if (!ctx?.serverTime) return;
        siteDateAppliedRef.current = true;
        const siteToday = formatDateInZone(ctx.serverTime, ctx.timezoneId);
        if (siteToday !== selectedDate) {
            setSelectedDate(siteToday);
        }
    }, [compareResponse, selectedDate]);

    return (
        <Column spacing="500" spacingInset="500">
            <Row spacing="500" alignmentVertical="bottom" alignmentHorizontal="justify" width="100%">
                <Row spacing="500" alignmentVertical="bottom" widths="fit">
                    <Column spacing="300">
                        <Text tag="label" type="b400">Date</Text>
                        <DatePicker
                            value={selectedDate}
                            onChange={(date: string) => {
                                // A manual date change wins over the pending site-local default,
                                // even if the first response hasn't landed yet.
                                siteDateAppliedRef.current = true;
                                setSelectedDate(date);
                            }}
                            disabledDates={(date: string) => date < MIN_SELECTABLE_DATE}
                            width={220}
                            size="large"
                        />
                    </Column>
                    {!isEndOfShift && (
                        <Column spacing="300">
                            <Text tag="label" type="b400">Shift</Text>
                            <Select value={shiftType} onChange={setShiftType} width={220} size="large">
                                {DEFAULT_SHIFT_OPTIONS.map((opt) => (
                                    <SelectOption key={opt.value} value={opt.value} label={opt.label} />
                                ))}
                            </Select>
                        </Column>
                    )}
                    <Column spacing="300">
                        <Text tag="label" type="b400">View</Text>
                        <Select data-testid="view-select" value={shiftReportView} onChange={setShiftReportView} width={200} size="large">
                            <SelectOption value="inShift" label="In-Shift" />
                            {isEosEnabled && <SelectOption value="endOfShift" label="End of Shift" />}
                        </Select>
                    </Column>
                    {/* End of Shift renders its own picker (it spans both shifts). */}
                    {!isEndOfShift && (
                        <ReportPlanSelector
                            groups={planGroups}
                            loading={plansLoading}
                            value={selectedPlanId ?? resolveDefaultPlanId(
                                planGroups,
                                compareResponse?.compareFlowMonitoringMetadata?.baselineShiftPlanId,
                            )}
                            onChange={setSelectedPlanId}
                            testId="in-shift-plan-select"
                        />
                    )}
                </Row>
                {lastUpdatedMs !== null && (
                    <Column spacing="100" alignmentHorizontal="end">
                        <Text tag="label" type="b400" color="secondary">Last updated</Text>
                        <Text type="b300">{formatTimestamp(lastUpdatedMs)}</Text>
                    </Column>
                )}
            </Row>

            {isEndOfShift ? (
                <EndOfShiftReporting ofdDate={selectedDate} />
            ) : (
            <>
            {loading && (
                <Column alignmentHorizontal="center" spacing="300" spacingInset="500">
                    <Loader size="large" />
                    <Text type="b300" color="secondary">Loading data...</Text>
                </Column>
            )}

            {dataLoaded && !loading && (
                <>
                    <Row alignmentHorizontal="justify" alignmentVertical="center">
                        <Heading level={2} type="h400">Progress Report</Heading>
                    </Row>

                    <ShiftProgressBanner projection={volumeProjection} />
                    <ShiftTimeline shiftTimeContext={compareResponse?.shiftTimeContext} />

                    <MaestroCardsRow
                        projection={volumeProjection}
                        shiftKpiComparisons={compareResponse?.shiftKpiComparisons}
                        shiftTimeContext={compareResponse?.shiftTimeContext}
                        nowMs={nowMs}
                    />

                    {compareResponse && (
                        <IntraShiftTrendChart compareResponse={compareResponse} />
                    )}

                    {compareResponse && <ProgressReportTable compareResponse={compareResponse} />}

                    {compareResponse && (
                        <InShiftEosWash
                            baselineShiftPlanId={compareResponse.compareFlowMonitoringMetadata?.baselineShiftPlanId}
                            report={compareResponse.eosWashReport}
                            testId="in-shift-eos-wash"
                        />
                    )}
                </>
            )}
            </>
            )}
        </Column>
    );
};

export default Dashboard;
